import { classMap, Component, html, LitElement, property, state, unsafeCSS } from '@rxdi/lit-html';
import * as BashEditorComponentCSS from 'bundle-text:./bash-editor.component.css';

type MutableValidity = {
  -readonly [K in keyof ValidityState]: ValidityState[K];
};

/**
 * @customElement bash-editor
 */
@Component({
  selector: 'bash-editor',
  styles: [unsafeCSS(BashEditorComponentCSS)],
  template(this: BashEditorComponent) {
    return html`
      <div class="editor-wrapper" part="editor-wrapper">
        <textarea
          class=${classMap({ outline: !this.readonly })}
          part="textarea"
          spellcheck="false"
          ?disabled="${this.disabled}"
          ?readonly="${this.readonly}"
          ?required="${this.required}"
          .tabIndex="${this.disabled ? -1 : 0}"
          aria-label="${this.ariaLabel || 'Bash Script Editor'}"
          aria-invalid="${!this.isValid}"
          aria-required="${this.required}"
          @input="${this.handleInput}"
          @change="${this.handleChange}"
          @blur="${this.handleBlur}"
          @focus="${this.handleFocus}"
          @scroll="${this.syncScroll}"
          @keydown="${this.handleTab}"
        ></textarea>
        <pre class="highlight-layer" aria-hidden="true" .innerHTML="${this.highlightSyntax(this.value)}"></pre>
        <div class="status-bar">
          <span class="${this.isValid ? '' : 'error'}">
            ${this.isValid ? '✓ Valid Script' : `✗ ${this.customValidationMessage || 'Invalid Script'}`}
          </span>
          <span>${this.value.split('\n').length} lines • ${this.value.length} characters</span>
        </div>
      </div>
    `;
  },
})
export class BashEditorComponent extends LitElement {
  // Form-associated custom element
  static formAssociated = true;

  private _internals: ElementInternals;
  private _value = '#!/bin/bash\n\necho "Hello World"';
  private _customValidationMessage = '';
  private _isUserInput = false;

  @property({ type: String, reflect: true }) theme: 'black' | 'white' = 'black';
  @property({ type: String, reflect: true }) name = '';
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) validateShebang = true;
  @property({ type: Boolean, reflect: true }) autoScroll = false;
  @property({ type: String }) ariaLabel = '';
  @property({ type: String }) placeholder = '';
  @property({ type: Number }) minLength = 0;
  @property({ type: Number }) maxLength = Infinity;

  // Don't use @property decorator for value to avoid automatic requestUpdate
  get value(): string {
    return this._value;
  }
  set value(val: string) {
    val = val === null || val === undefined ? '' : String(val);

    const oldVal = this._value;
    if (val === oldVal) return;

    // Parse escape sequences like \n, \t, etc.
    this._value = this.parseEscapeSequences(val);

    // Update textarea directly without triggering full re-render
    if (!this._isUserInput) {
      const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
      if (textarea) {
        textarea.value = this._value;

        if (this.autoScroll) {
          this.scrollToBottom(textarea);
        }
      }
      // Only update highlight layer and status
      this.requestUpdate();
    }

    this.validateAndUpdateForm();
  }

  @state() private isValid = true;
  @state() private customValidationMessage = '';

  constructor() {
    super();
    this._internals = this.attachInternals();
  }

  OnUpdateFirst() {
    // Parse escape sequences on first load
    this._value = this.parseEscapeSequences(this._value);
    this.validateAndUpdateForm();

    // Set initial textarea value
    const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    if (textarea) {
      textarea.value = this._value;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'textbox');
    }
  }

  private scrollToBottom(textarea: HTMLTextAreaElement) {
    setTimeout(() => {
      textarea.scrollTop = textarea.scrollHeight;
      this.syncScroll();
    });
  }

  /**
   * Parse escape sequences like \n, \t, etc. into actual characters
   */
  private parseEscapeSequences(str: string): string {
    if (!str) return str;

    return str.replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\r/g, '\r').replace(/\\\\/g, '\\');
  }

  updated(changedProperties: Map<string, string>) {
    super.updated(changedProperties);

    // Reset the flag after update
    this._isUserInput = false;
  }

  private validateAndUpdateForm() {
    const validityState: Partial<MutableValidity> = {};
    let validationMessage = '';

    // Check required
    if (this.required && (!this._value || this._value.trim() === '')) {
      validityState.valueMissing = true;
      validationMessage = 'This field is required';
    }
    // Check custom validation message
    else if (this._customValidationMessage) {
      validityState.customError = true;
      validationMessage = this._customValidationMessage;
    }
    // Check bash script validity
    else {
      const validation = this.validateBashScript(this._value);
      this.isValid = validation.isValid;

      if (!validation.isValid) {
        validityState.customError = true;
        validationMessage = validation.message || 'Invalid bash script';
      } else {
        // Check length constraints
        if (this.minLength && this._value.length < this.minLength) {
          validityState.tooShort = true;
          validationMessage = `Minimum length is ${this.minLength} characters`;
        } else if (this.maxLength && this._value.length > this.maxLength) {
          validityState.tooLong = true;
          validationMessage = `Maximum length is ${this.maxLength} characters`;
        }
      }
    }

    // Update form internals
    if (Object.keys(validityState).length > 0) {
      this._internals.setValidity(
        validityState,
        validationMessage,
        this.shadowRoot?.querySelector('textarea') || undefined
      );
    } else {
      this._internals.setValidity({});
    }

    this._internals.setFormValue(this._value);
    this.customValidationMessage = validationMessage;
  }

  private validateBashScript(script: string): {
    isValid: boolean;
    message?: string;
  } {
    if (!script || script.trim() === '') {
      return { isValid: true };
    }

    // Check for shebang if required
    if (this.validateShebang) {
      const lines = script.split('\n');
      const firstLine = lines[0].trim();
      if (!firstLine.startsWith('#!')) {
        return {
          isValid: false,
          message: 'Script must start with shebang (#!/bin/bash or #!/bin/sh)',
        };
      }
      if (!firstLine.match(/^#!\s*\/.*\/(bash|sh|env\s+(bash|sh))/)) {
        return {
          isValid: false,
          message: 'Invalid shebang. Use #!/bin/bash or #!/bin/sh',
        };
      }
    }

    // Basic syntax checks
    const errors = this.checkBashSyntax(script);
    if (errors.length > 0) {
      return { isValid: false, message: errors[0] };
    }

    return { isValid: true };
  }

  private checkBashSyntax(script: string): string[] {
    const errors: string[] = [];
    const lines = script.split('\n');

    // Track brackets and quotes
    let singleQuoteOpen = false;
    let doubleQuoteOpen = false;
    let braceCount = 0;
    let parenCount = 0;
    let bracketCount = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const lineNum = i + 1;

      // Skip comments (but not shebang)
      if (line.trim().startsWith('#') && i !== 0) continue;

      // Check for unmatched quotes and brackets
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        const prevChar = j > 0 ? line[j - 1] : '';

        // Handle quotes
        if (char === "'" && prevChar !== '\\' && !doubleQuoteOpen) {
          singleQuoteOpen = !singleQuoteOpen;
        }
        if (char === '"' && prevChar !== '\\' && !singleQuoteOpen) {
          doubleQuoteOpen = !doubleQuoteOpen;
        }

        // Handle brackets (only outside quotes)
        if (!singleQuoteOpen && !doubleQuoteOpen) {
          if (char === '{') braceCount++;
          if (char === '}') braceCount--;
          if (char === '(') parenCount++;
          if (char === ')') parenCount--;
          if (char === '[') bracketCount++;
          if (char === ']') bracketCount--;
        }
      }

      // Check for common syntax errors
      if (line.match(/\bif\b/) && !line.match(/;\s*then/) && !lines[i + 1]?.match(/^\s*then/)) {
        if (!line.includes('[[') && !line.includes('[')) {
          errors.push(`Line ${lineNum}: 'if' statement missing 'then'`);
        }
      }

      if (line.match(/\bfor\b/) && !line.match(/;\s*do/) && !lines[i + 1]?.match(/^\s*do/)) {
        errors.push(`Line ${lineNum}: 'for' loop missing 'do'`);
      }

      if (line.match(/\bwhile\b/) && !line.match(/;\s*do/) && !lines[i + 1]?.match(/^\s*do/)) {
        errors.push(`Line ${lineNum}: 'while' loop missing 'do'`);
      }
    }

    // Check for unmatched delimiters
    if (singleQuoteOpen) {
      errors.push('Unmatched single quote');
    }
    if (doubleQuoteOpen) {
      errors.push('Unmatched double quote');
    }
    if (braceCount !== 0) {
      errors.push(`Unmatched braces: ${braceCount > 0 ? 'missing }' : 'extra }'}`);
    }
    if (parenCount !== 0) {
      errors.push(`Unmatched parentheses: ${parenCount > 0 ? 'missing )' : 'extra )'}`);
    }
    if (bracketCount !== 0) {
      errors.push(`Unmatched brackets: ${bracketCount > 0 ? 'missing ]' : 'extra ]'}`);
    }

    return errors;
  }

  private handleInput(e: Event) {
    if (this.readonly) return;

    const target = e.target as HTMLTextAreaElement;
    this._isUserInput = true;
    this._value = target.value;

    this.validateAndUpdateForm();
    this.syncScroll();

    // Only update the highlight layer and status bar, not the textarea
    this.requestUpdate();

    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));

    if (this.isValid) {
      this.sendChangeEvent();
    }
    this.dispatchValidityEvent();
  }

  private handleChange(e: Event) {
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  private handleBlur(e: FocusEvent) {
    this.dispatchEvent(new FocusEvent('blur', { bubbles: true, composed: true }));
  }

  private handleFocus(e: FocusEvent) {
    this.dispatchEvent(new FocusEvent('focus', { bubbles: true, composed: true }));
  }

  private sendChangeEvent() {
    this.dispatchEvent(
      new CustomEvent('onValueChange', {
        detail: this._value,
        bubbles: true,
        composed: true,
      })
    );
  }

  private dispatchValidityEvent() {
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: this.isValid,
        bubbles: true,
        composed: true,
      })
    );
  }

  private syncScroll() {
    const textarea = this.shadowRoot?.querySelector('textarea');
    const highlight = this.shadowRoot?.querySelector('.highlight-layer');
    if (textarea && highlight) {
      highlight.scrollTop = textarea.scrollTop;
      highlight.scrollLeft = textarea.scrollLeft;
    }
  }

  private handleTab(e: KeyboardEvent) {
    if (this.readonly) return;

    if (e.key === 'Tab' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();

      // Use execCommand to preserve undo history
      document.execCommand('insertText', false, '  ');
    }
  }
  private highlightSyntax(script: string): string {
    return script
      .split('\n')
      .map((line, index) => this.highlightLine(line, index === 0))
      .join('\n');
  }

  private highlightLine(line: string, isFirstLine: boolean): string {
    let escaped = line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Shebang
    if (isFirstLine && escaped.startsWith('#!')) {
      return `<span class="shebang">${escaped}</span>`;
    }

    // Comment (must be first!)
    if (escaped.trim().startsWith('#')) {
      return `<span class="comment">${escaped}</span>`;
    }

    // Strings
    escaped = escaped.replace(/"(?:[^"\\]|\\.)*"/g, '<span class="string">$&</span>');
    escaped = escaped.replace(/'(?:[^'\\]|\\.)*'/g, '<span class="string">$&</span>');

    // Variables
    escaped = escaped.replace(/(\$\{?[a-zA-Z_][a-zA-Z0-9_]*\}?|\$[0-9@*#?$!-])/g, '<span class="variable">$&</span>');

    // Keywords
    escaped = escaped.replace(
      /\b(if|then|else|elif|fi|for|while|do|done|case|esac|function|in|select|time|until|return|exit|break|continue)\b/g,
      '<span class="keyword">$&</span>'
    );

    // Commands
    escaped = escaped.replace(/\b(echo|cd|mv|cp|rm|npm|npx|git|curl|wget|ssh)\b/g, '<span class="command">$&</span>');

    return escaped;
  }

  // ============================================
  // FORM ELEMENT API
  // ============================================

  formResetCallback() {
    this._value = '#!/bin/bash\n\necho "Hello World"';
    this._customValidationMessage = '';
    this.isValid = true;
    this.validateAndUpdateForm();
    this.requestUpdate();
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }

  formStateRestoreCallback(state: string, mode: 'restore' | 'autocomplete') {
    this._value = state;
    this.validateAndUpdateForm();
  }

  get form() {
    return this._internals.form;
  }

  get type() {
    return 'bash-editor';
  }

  get validity() {
    return this._internals.validity;
  }

  get validationMessage() {
    return this._internals.validationMessage;
  }

  get willValidate() {
    return this._internals.willValidate;
  }

  checkValidity(): boolean {
    return this._internals.checkValidity();
  }

  reportValidity(): boolean {
    return this._internals.reportValidity();
  }

  setCustomValidity(message: string): void {
    this._customValidationMessage = message;
    this.validateAndUpdateForm();
  }

  focus(options?: FocusOptions): void {
    const textarea = this.shadowRoot?.querySelector('textarea');
    textarea?.focus(options);
  }

  blur(): void {
    const textarea = this.shadowRoot?.querySelector('textarea');
    textarea?.blur();
  }

  select(): void {
    const textarea = this.shadowRoot?.querySelector('textarea');
    textarea?.select();
  }

  setSelectionRange(start: number, end: number, direction?: 'forward' | 'backward' | 'none'): void {
    const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    textarea?.setSelectionRange(start, end, direction);
  }

  setRangeText(replacement: string, start?: number, end?: number, selectMode?: SelectionMode): void {
    const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    if (textarea) {
      if (start !== undefined && end !== undefined) {
        textarea.setRangeText(replacement, start, end, selectMode);
      } else {
        textarea.setRangeText(replacement);
      }
      this._value = textarea.value;
      this.validateAndUpdateForm();
    }
  }

  get selectionStart(): number | null {
    const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    return textarea?.selectionStart ?? null;
  }

  get selectionEnd(): number | null {
    const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    return textarea?.selectionEnd ?? null;
  }

  get selectionDirection(): string | null {
    const textarea = this.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    return textarea?.selectionDirection ?? null;
  }

  get labels() {
    return this._internals.labels;
  }
}
